import { useCallback, useContext } from 'react';
import BoardsContext from 'contexts/BoardsContext';
import PopupContext from 'contexts/PopupContext';
import ReportsContext from 'contexts/ReportsContext';
import ScorecardContext from 'contexts/ScorecardContext';
import { isKpiRow, isManualKpiRow } from 'hooks/kpiTypeCheckers';
import getCurrentPeriodEndDate from 'contextProviders/ScorecardProvider/getCurrentPeriodEndDate';
import { AccountTodayOverrideContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/AccountTodayOverrideProvider';
import EntityDetailsContext from '../../../../screens/EntityDetailsShow/EntityDetailsContext';

const useOpenKpiPopup = (
  kpi: Scorecards.ScorecardKpi | Scorecards.ManualKpiRow | undefined,
  period: Period | undefined,
) => {
  const { allReports } = useContext(ReportsContext);
  const { boards } = useContext(BoardsContext);
  const entityDetailsContext = useContext(EntityDetailsContext);
  const { openPopupReport, openPopupBoard } = useContext(PopupContext);
  const { setPoppedUpKpi, periods, futurePeriods, scorecard } =
    useContext(ScorecardContext);
  const { todayOverride } = useContext(AccountTodayOverrideContext);
  // Timeline events use this hook outside a ScorecardProvider, where the
  // context is empty.
  const currentPeriodEndDate = scorecard?.currentPeriodEndDate;

  const openKpiPopup = useCallback(() => {
    if (!kpi || !period) {
      return;
    }

    if (kpi.reportDrillDownId || kpi.boardDrillDownId) {
      setPoppedUpKpi(kpi);
    }

    if (kpi.reportDrillDownId) {
      const report = allReports.find((r) => r.id === kpi.reportDrillDownId);
      if (report) {
        // Match the KPI List's own data: future periods and "Entire period"
        // lists run to the period end, the in-progress period stops at
        // today or yesterday.
        const endDate = (() => {
          const isFuturePeriod = futurePeriods.some(
            (p) => p.startDate === period.startDate,
          );
          if (isFuturePeriod || currentPeriodEndDate === 'period') {
            return period.endDate;
          }
          const currentEndDate = getCurrentPeriodEndDate({
            currentPeriodEndDate,
            todayOverride,
          });
          return period.endDate > currentEndDate
            ? currentEndDate
            : period.endDate;
        })();

        const scope: FilterPlateType[] = [];
        if (isKpiRow(kpi)) {
          if (entityDetailsContext && !kpi.isEntityFiltersDisabled) {
            scope.push(...entityDetailsContext.entityFilterAsFilterPlates);
          }
          scope.push(...kpi.scopeDrillDowns);
        }
        if (isManualKpiRow(kpi) && entityDetailsContext) {
          scope.push(...entityDetailsContext.entityFilterAsFilterPlates);
        }

        const unSavedFilter: UnSavedFilter = {
          drillDowns: isKpiRow(kpi) ? kpi.drillDowns : window.emptyArray,
          dateScope: {},
          scope,
          dateField: isKpiRow(kpi) ? kpi.queryDateField || 'date' : 'date',
          dateRange: {
            startDate: period.startDate,
            endDate,
          },
        };

        openPopupReport({
          drillDowns: [],
          origin: 'Scorecard',
          scorecardPeriodSettings: {
            availablePeriods: periods,
            selectedPeriod: period,
            kpi,
          },
          selectedReport: report,
          unSavedFilter,
        });
      }
    } else if (isKpiRow(kpi) && kpi.boardDrillDownId) {
      const board = boards.find((b) => b.id === kpi.boardDrillDownId);
      if (board) {
        const scope: FilterPlateType[] = [];
        if (isKpiRow(kpi)) {
          if (entityDetailsContext && !kpi.isEntityFiltersDisabled) {
            scope.push(...entityDetailsContext.entityFilterAsFilterPlates);
          }
          scope.push(...kpi.scopeDrillDowns);
        }

        const dateRange = {
          startDate: period.startDate,
          endDate: period.endDate,
        };

        openPopupBoard({
          popupFilters: kpi.drillDowns,
          scope,
          popupDateRange: dateRange,
          origin: 'Goal',
          scorecardPeriodSettings: {
            availablePeriods: periods,
            selectedPeriod: period,
            kpi,
          },
          selectedBoard: board,
        });
      }
    }
  }, [
    kpi,
    period,
    setPoppedUpKpi,
    allReports,
    entityDetailsContext,
    futurePeriods,
    currentPeriodEndDate,
    todayOverride,
    openPopupReport,
    periods,
    boards,
    openPopupBoard,
  ]);

  return kpi && period ? openKpiPopup : undefined;
};

export default useOpenKpiPopup;
